/* eslint-disable no-func-assign */
import { ComponentType, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Power1 } from 'gsap';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';

import LineAnimation from '@/services/animations/LineAnimation';
import { signInValidate } from '@/services/FormValidate';
import { setInitialEmail } from '../../../forgotPassword/store/actions';
import { SIGN_IN_FORM } from '../../store/constants';

import Button from '@/components/button/Button';
import { renderFormInput } from '@/components/FormFields';
import ButtonLine from '@/components/lines/ButtonLine';

interface SignInFormData {
	email_or_username: string;
	password: string;
}

interface OwnProps {
	loading: boolean;
	onFormSubmit: (email_or_username: string, password: string) => void;
}

type Props = OwnProps & InjectedFormProps<SignInFormData, OwnProps>;

const SignInFormBase = ({ handleSubmit, onFormSubmit, valid, loading }: Props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const onSubmitForm = ({ email_or_username, password }: SignInFormData) => {
		onFormSubmit(email_or_username, password);
	};

	return (
		<form onSubmit={handleSubmit(onSubmitForm)} data-testid="auth-form">
			<Field
				name="email_or_username"
				type="text"
				component={renderFormInput}
				label={t('sign_in.e_mail_or_username')}
				floatingLabel
				noBlur={false}
				onChange={(_e: any, newValue: string) => dispatch(setInitialEmail(newValue))}
			/>
			<Field
				name="password"
				type="password"
				component={renderFormInput}
				label={t('sign_in.password')}
				floatingLabel
				noBlur={false}
			/>
			<div className="btn-group right">
				<Button
					onClick={loading ? undefined : handleSubmit(onSubmitForm)}
					type="primary"
					title={t('sign_in.login')}
					disabled={!valid}
					loading={loading}
					dataName="sign_in_btn"
				/>
				{valid && (
					<LineAnimation
						lineId="#button-line"
						duration={0.6}
						delay={0}
						ease={Power1.easeIn}
					>
						<ButtonLine className="button-line" />
					</LineAnimation>
				)}
			</div>
		</form>
	);
};

const SignInFormConnected = reduxForm<SignInFormData, OwnProps>({
	form: SIGN_IN_FORM,
	validate: signInValidate,
})(memo(SignInFormBase) as any) as ComponentType<
	OwnProps & { initialValues?: Partial<SignInFormData> }
>;

const SignInForm = (props: OwnProps) => {
	const initialEmail = useSelector((state: any) => state.forgotPasswordReducer.initialEmail);

	return <SignInFormConnected {...props} initialValues={{ email_or_username: initialEmail }} />;
};

export default SignInForm;
